import { CRMChannel, useRestApiProvider } from "@jugl-web/rest-api";
import { Menu, PlainButton } from "@jugl-web/ui-components";
import { useTranslations } from "@jugl-web/utils";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import React, { useCallback, useState } from "react";
import { whatsappConversations$ } from "@jugl-web/rest-api/integrations/hooks/useWhatsappConversations";
import { instagramConversations$ } from "@jugl-web/rest-api/integrations/hooks/useInstagramConversations";
import { webChatBotConversations$ } from "@jugl-web/rest-api/integrations/hooks/useWebChatBotConversations";
import { twilioConversations$ } from "@jugl-web/rest-api/integrations/hooks/useTwilioConversations";
import { useClientSupportPageContext } from "../../../../providers/ClientSupportPageProvider/useClientSupportPageContext";
import { formatWhatsappPhoneNumber } from "../../utils/formatWhatsappPhoneNumber";
import { useWhatsappAccount } from "../../../../providers/WhatsappAccountProvider/useWhatsappAccount";
import { ReactComponent as BlockIcon } from "./assets/block.svg";
import { ReactComponent as MoreIcon } from "./assets/more.svg";
import { ReactComponent as UnblockIcon } from "./assets/unblock.svg";
import { ManageContactAlert } from "../ManageContactAlert";

export const ConversationContactMenu: React.FC<{
  isContactBlocked: boolean;
  isChatExpired: boolean;
}> = ({ isContactBlocked, isChatExpired }) => {
  const [isManageAccountAlertOpen, setIsManageAccountAlertOpen] =
    useState(false);
  const { t } = useTranslations();
  const { integrationsApi } = useRestApiProvider();
  const { entityId } = useEntitySelectedProvider();
  const {
    selectedConversation,
    refetchTrigger$,
    selectConversation,
    selectedChannel,
  } = useClientSupportPageContext();
  const { whatsappAgent } = useWhatsappAccount();

  const [manageContact, { isLoading: isManageContactLoading }] =
    integrationsApi.useManageContactMutation();

  const formattedPhoneNumber = selectedConversation
    ? formatWhatsappPhoneNumber(
        selectedConversation.conv_id,
        whatsappAgent?.settings.hide_num || false
      )
    : "";

  const handleManageContact = useCallback(async () => {
    if (!selectedConversation) {
      return;
    }

    const response = await manageContact({
      entityId,
      userId: selectedConversation.conv_id,
      type: isContactBlocked ? "unblock" : "block",
    });

    if (response && "data" in response) {
      const conversationId = selectedConversation.conv_id;

      switch (selectedChannel) {
        case CRMChannel.whatsapp:
          whatsappConversations$.next({ type: "remove", conversationId });
          break;
        case CRMChannel.instagram:
          instagramConversations$.next({ type: "remove", conversationId });
          break;
        case CRMChannel.chatbot:
          webChatBotConversations$.next({ type: "remove", conversationId });
          break;
        case CRMChannel.twilio:
          twilioConversations$.next({ type: "remove", conversationId });
          break;
        default:
          break;
      }

      refetchTrigger$.next();
      selectConversation(undefined);
    }
  }, [
    selectedConversation,
    refetchTrigger$,
    manageContact,
    entityId,
    isContactBlocked,
    selectConversation,
    selectedChannel,
  ]);

  if (!selectedConversation) {
    return null;
  }

  if (isManageContactLoading || (isChatExpired && !isContactBlocked)) {
    return null;
  }

  return (
    <>
      <Menu
        placement="bottom-end"
        autoClose
        onSelect={() => setIsManageAccountAlertOpen(true)}
        sections={[
          [
            {
              id: "block-user",
              icon: <BlockIcon />,
              label: t({
                id: "client-support-page.block-user",
                defaultMessage: "Block User",
              }),
              isHidden: isContactBlocked,
            },
            {
              id: "unblock-user",
              icon: <UnblockIcon />,
              label: t({
                id: "client-support-page.unblock-user",
                defaultMessage: "Unblock User",
              }),
              isHidden: !isContactBlocked,
            },
          ],
        ]}
        renderTrigger={({ Trigger, triggerRef }) => (
          <Trigger
            ref={triggerRef}
            as={PlainButton}
            className="hover:bg-dark/5 flex h-8 w-8 items-center justify-center rounded-full transition-colors"
            onClick={(event) => event.stopPropagation()}
          >
            <MoreIcon />
          </Trigger>
        )}
      />
      <ManageContactAlert
        isOpen={isManageAccountAlertOpen}
        onConfirm={() => {
          handleManageContact();
          setIsManageAccountAlertOpen(false);
        }}
        onRequestClose={() => setIsManageAccountAlertOpen(false)}
        type={isContactBlocked ? "unblock" : "block"}
        phoneNumber={formattedPhoneNumber}
      />
    </>
  );
};
